import type { Tone } from "@amtana/design-system";
import type { Catalogs, Formats, PlainKey, Translation } from "@amtana/i18n/catalog";
import type {
  DnsStatus,
  MailboxStatus,
  OrderStatus,
  OrderStep,
  Vendor,
} from "@amtana/managed-mailbox-contract";

// The words of the Managed Mailboxes screens (domains, mailboxes, orders, one order)
// and the labels every screen shares, in English and Spanish. The purchase wizard's
// words live in `managed-mailbox-purchase-copy.ts`. A table of labels holds keys,
// not words; a screen reads them through `useManagedMailboxCopy`.

export type ManagedMailboxCopyKey = keyof typeof EN;
export type ManagedMailboxCatalog = typeof EN;

const EN = {
  titleDomains: "My Domains · {product}",
  titleMailboxes: "My Mailboxes · {product}",
  titleOrders: "My Orders · {product}",
  titleOrder: "Order · {product}",
  breadcrumbHome: "Home",
  breadcrumbSenders: "Senders",
  breadcrumbManagedMailboxes: "Managed Mailboxes",
  breadcrumbDomains: "Domains",
  breadcrumbMailboxes: "Mailboxes",
  breadcrumbOrders: "Orders",
  vendorSmtp: "SMTP",
  vendorGoogle: "Google Workspace",
  vendorMicrosoft: "Microsoft 365",
  statusAwaitingPayment: "Awaiting payment",
  statusProcessing: "Processing",
  statusDelivered: "Delivered",
  statusPartial: "Partial",
  statusFailed: "Failed",
  statusPaymentFailed: "Payment Failed",
  statusCancelled: "Cancelled",
  statusInProgress: "In progress",
  statusPending: "Pending",
  statusWarming: "Warming",
  statusActive: "Active",
  statusCanceling: "Canceling...",
  statusCanceled: "Canceled",
  dnsPropagated: "Propagated",
  dnsVerified: "Verified",
  dnsRecordOk: "{record} OK",
  dnsRecordMissing: "{record} Missing",
  stepPaidTitle: "Payment confirmed",
  stepPaidHint: "Confirming the payment",
  stepDomainTitle: "Domain registered",
  stepDomainHint: "Registering the domain in your name",
  stepDnsTitle: "Setting up DNS",
  stepDnsHint: "SPF, DKIM and DMARC so your emails stay out of spam",
  stepMailboxesTitle: "Creating mailboxes",
  stepMailboxesHint: "Setting up the addresses",
  stepWarmupTitle: "Starting warm-up",
  stepWarmupHint: "Connecting the mailboxes to warm-up",
  labelDomain: "Domain",
  labelDomains: "Domains",
  labelMailboxes: "Mailboxes",
  labelProvider: "Provider",
  labelStatus: "Status",
  labelActions: "Actions",
  labelRenewalDate: "Renewal Date",
  labelEmail: "Email",
  labelSender: "Sender",
  labelTotal: "Total",
  labelNotes: "Notes",
  labelOrder: "Order",
  actionsFor: "Actions for {name}",
  refresh: "Refresh",
  domainsPageTitle: "My Domains",
  domainsPageDescription: "Manage your purchased domains and mailboxes",
  buyDomain: "Buy Domain",
  noChangesDetected: "No changes detected",
  statDomainsTotal: "Domains total",
  statTotalMailboxes: "Total Mailboxes",
  statActiveDomains: "Active Domains",
  searchDomainsPlaceholder: "Search domains or mailboxes...",
  noDomainsTitle: "You haven't purchased any domains yet",
  purchaseFirstDomain: "Purchase Your First Domain",
  columnDnsStatus: "DNS Status",
  viewOrder: "View order",
  viewMailboxes: "View Mailboxes",
  deleteDomain: "Delete Domain",
  thisDomain: "this domain",
  deleteDomainDescription: {
    one: "Are you sure you want to delete {domain}? This will cancel your subscription and deactivate all mailboxes associated with it. The mailbox will be permanently deactivated.",
    other:
      "Are you sure you want to delete {domain}? This will cancel your subscription and deactivate all mailboxes associated with it. All {count} mailboxes will be permanently deactivated.",
  },
  domainDeleted: "Domain deleted successfully",
  couldNotDeleteDomain: "Could not delete the domain",
  mailboxesPageTitle: "My Mailboxes",
  mailboxesPageDescription: "Manage your purchased mailboxes and sender accounts",
  buyMailbox: "Buy Mailbox",
  statActiveMailboxes: "Active Mailboxes",
  searchMailboxesPlaceholder: "Search mailboxes by email or name...",
  noMailboxesTitle: "No mailboxes configured for this domain",
  purchaseFirstMailbox: "Purchase Your First Mailbox",
  columnName: "Name",
  columnPlatform: "Platform",
  viewSender: "View Sender",
  deleteMailbox: "Delete Mailbox",
  deleteMailboxDescription:
    "Are you sure you want to delete {email}? It will be deactivated immediately and you will stop being charged for it.",
  deleteMailboxLastDescription:
    "Are you sure you want to delete {email}? It will be deactivated immediately and you will stop being charged for it. This is the last mailbox on this domain. Deleting it will also cancel the domain subscription.",
  mailboxDeleted: "Mailbox deleted successfully",
  couldNotDeleteMailbox: "Could not delete the mailbox",
  ordersPageTitle: "My Orders",
  ordersPageDescription: "View and manage your domain and mailbox orders",
  newOrder: "New Order",
  noOrdersTitle: "No Orders Yet",
  noOrdersDescription:
    "You haven't placed any orders yet. Start by purchasing your first domain and mailboxes.",
  createFirstOrder: "Create Your First Order",
  viewDetails: "View Details",
  infrastructureReady: "Infrastructure ready",
  processingYourOrder: "Processing your order…",
  provisioningSteps: "Provisioning steps",
  orderProgress: "Order progress",
  timeLeft:
    "Estimated time left: {count} min · You can close this window. We will email you when it is ready.",
  mailboxesReady: {
    one: "{ready} of {count} mailbox ready",
    other: "{ready} of {count} mailboxes ready",
  },
  errorLoadingOrder: "Error loading order",
  backToOrders: "Back to Orders",
  orderTitle: "Order {id}",
  orderPageDescription: "View order details and mailbox credentials",
  labelVendor: "Vendor",
  labelCreated: "Created",
  labelDelivered: "Delivered",
  mailboxesConnectTitle: "Mailboxes connect as email accounts on their own",
  mailboxesConnectDescription:
    "Their passwords stay encrypted on our servers and are never shown. Mailboxes auto-renew monthly.",
  columnSenderConnection: "Sender Connection",
  credentialsReady: "Credentials Ready",
} as const;

const ES: Translation<ManagedMailboxCatalog> = {
  titleDomains: "Mis dominios · {product}",
  titleMailboxes: "Mis buzones · {product}",
  titleOrders: "Mis pedidos · {product}",
  titleOrder: "Pedido · {product}",
  breadcrumbHome: "Inicio",
  breadcrumbSenders: "Remitentes",
  breadcrumbManagedMailboxes: "Buzones gestionados",
  breadcrumbDomains: "Dominios",
  breadcrumbMailboxes: "Buzones",
  breadcrumbOrders: "Pedidos",
  vendorSmtp: "SMTP",
  vendorGoogle: "Google Workspace",
  vendorMicrosoft: "Microsoft 365",
  statusAwaitingPayment: "Pendiente de pago",
  statusProcessing: "Procesando",
  statusDelivered: "Entregado",
  statusPartial: "Parcial",
  statusFailed: "Fallido",
  statusPaymentFailed: "Pago fallido",
  statusCancelled: "Cancelado",
  statusInProgress: "En curso",
  statusPending: "Pendiente",
  statusWarming: "Calentando",
  statusActive: "Activo",
  statusCanceling: "Cancelando...",
  statusCanceled: "Cancelado",
  dnsPropagated: "Propagado",
  dnsVerified: "Verificado",
  dnsRecordOk: "{record} OK",
  dnsRecordMissing: "Falta {record}",
  stepPaidTitle: "Pago confirmado",
  stepPaidHint: "Confirmando el pago",
  stepDomainTitle: "Dominio registrado",
  stepDomainHint: "Registrando el dominio a tu nombre",
  stepDnsTitle: "Configurando el DNS",
  stepDnsHint: "SPF, DKIM y DMARC para que tus correos no lleguen a spam",
  stepMailboxesTitle: "Creando buzones",
  stepMailboxesHint: "Configurando las direcciones",
  stepWarmupTitle: "Iniciando el calentamiento",
  stepWarmupHint: "Conectando los buzones al calentamiento",
  labelDomain: "Dominio",
  labelDomains: "Dominios",
  labelMailboxes: "Buzones",
  labelProvider: "Proveedor",
  labelStatus: "Estado",
  labelActions: "Acciones",
  labelRenewalDate: "Fecha de renovación",
  labelEmail: "Correo",
  labelSender: "Remitente",
  labelTotal: "Total",
  labelNotes: "Notas",
  labelOrder: "Pedido",
  actionsFor: "Acciones para {name}",
  refresh: "Actualizar",
  domainsPageTitle: "Mis dominios",
  domainsPageDescription: "Gestiona tus dominios y buzones comprados",
  buyDomain: "Comprar dominio",
  noChangesDetected: "No se detectaron cambios",
  statDomainsTotal: "Dominios en total",
  statTotalMailboxes: "Buzones en total",
  statActiveDomains: "Dominios activos",
  searchDomainsPlaceholder: "Buscar dominios o buzones...",
  noDomainsTitle: "Aún no has comprado ningún dominio",
  purchaseFirstDomain: "Compra tu primer dominio",
  columnDnsStatus: "Estado del DNS",
  viewOrder: "Ver pedido",
  viewMailboxes: "Ver buzones",
  deleteDomain: "Eliminar dominio",
  thisDomain: "este dominio",
  deleteDomainDescription: {
    one: "¿Seguro que quieres eliminar {domain}? Esto cancelará tu suscripción y desactivará todos los buzones asociados. El buzón se desactivará de forma permanente.",
    other:
      "¿Seguro que quieres eliminar {domain}? Esto cancelará tu suscripción y desactivará todos los buzones asociados. Los {count} buzones se desactivarán de forma permanente.",
  },
  domainDeleted: "Dominio eliminado correctamente",
  couldNotDeleteDomain: "No se pudo eliminar el dominio",
  mailboxesPageTitle: "Mis buzones",
  mailboxesPageDescription: "Gestiona tus buzones comprados y tus cuentas de remitente",
  buyMailbox: "Comprar buzón",
  statActiveMailboxes: "Buzones activos",
  searchMailboxesPlaceholder: "Buscar buzones por correo o nombre...",
  noMailboxesTitle: "No hay buzones configurados para este dominio",
  purchaseFirstMailbox: "Compra tu primer buzón",
  columnName: "Nombre",
  columnPlatform: "Plataforma",
  viewSender: "Ver remitente",
  deleteMailbox: "Eliminar buzón",
  deleteMailboxDescription:
    "¿Seguro que quieres eliminar {email}? Se desactivará de inmediato y dejarás de pagar por él.",
  deleteMailboxLastDescription:
    "¿Seguro que quieres eliminar {email}? Se desactivará de inmediato y dejarás de pagar por él. Es el último buzón de este dominio. Al eliminarlo, también se cancelará la suscripción del dominio.",
  mailboxDeleted: "Buzón eliminado correctamente",
  couldNotDeleteMailbox: "No se pudo eliminar el buzón",
  ordersPageTitle: "Mis pedidos",
  ordersPageDescription: "Consulta y gestiona tus pedidos de dominios y buzones",
  newOrder: "Nuevo pedido",
  noOrdersTitle: "Aún no hay pedidos",
  noOrdersDescription:
    "Todavía no has hecho ningún pedido. Empieza comprando tu primer dominio y tus buzones.",
  createFirstOrder: "Crea tu primer pedido",
  viewDetails: "Ver detalles",
  infrastructureReady: "Infraestructura lista",
  processingYourOrder: "Procesando tu pedido…",
  provisioningSteps: "Pasos de la preparación",
  orderProgress: "Progreso del pedido",
  timeLeft:
    "Tiempo estimado restante: {count} min · Puedes cerrar esta ventana. Te avisaremos por correo cuando esté listo.",
  mailboxesReady: {
    one: "{ready} de {count} buzón listo",
    other: "{ready} de {count} buzones listos",
  },
  errorLoadingOrder: "Error al cargar el pedido",
  backToOrders: "Volver a pedidos",
  orderTitle: "Pedido {id}",
  orderPageDescription: "Consulta los detalles del pedido y las credenciales de los buzones",
  labelVendor: "Proveedor",
  labelCreated: "Creado",
  labelDelivered: "Entregado",
  mailboxesConnectTitle: "Los buzones se conectan solos como cuentas de correo",
  mailboxesConnectDescription:
    "Sus contraseñas quedan cifradas en nuestros servidores y nunca se muestran. Los buzones se renuevan cada mes.",
  columnSenderConnection: "Conexión del remitente",
  credentialsReady: "Credenciales listas",
};

export const MANAGED_MAILBOX_CATALOGS: Catalogs<ManagedMailboxCatalog> = { en: EN, es: ES };

type Label = PlainKey<ManagedMailboxCatalog>;

export const VENDOR_LABEL: Readonly<Record<Vendor, Label>> = {
  smtp: "vendorSmtp",
  google: "vendorGoogle",
  microsoft: "vendorMicrosoft",
};

export const VENDOR_MARK: Readonly<Record<Vendor, "smtp" | "gmail" | "outlook">> = {
  smtp: "smtp",
  google: "gmail",
  microsoft: "outlook",
};

export const ORDER_STATUS: Readonly<Record<OrderStatus, { label: Label; tone: Tone }>> = {
  awaiting_payment: { label: "statusAwaitingPayment", tone: "warning" },
  processing: { label: "statusProcessing", tone: "info" },
  delivered: { label: "statusDelivered", tone: "success" },
  partial: { label: "statusPartial", tone: "warning" },
  failed: { label: "statusFailed", tone: "danger" },
  payment_failed: { label: "statusPaymentFailed", tone: "danger" },
  cancelled: { label: "statusCancelled", tone: "neutral" },
};

export const MAILBOX_STATUS: Readonly<Record<MailboxStatus, { label: Label; tone: Tone }>> = {
  pending: { label: "statusPending", tone: "neutral" },
  processing: { label: "statusProcessing", tone: "info" },
  warming: { label: "statusWarming", tone: "warning" },
  active: { label: "statusActive", tone: "success" },
  canceling: { label: "statusCanceling", tone: "warning" },
  cancelled: { label: "statusCancelled", tone: "neutral" },
  failed: { label: "statusFailed", tone: "danger" },
};

export const DNS_STATUS: Readonly<Record<DnsStatus, { label: Label; tone: Tone }>> = {
  pending: { label: "statusPending", tone: "neutral" },
  propagated: { label: "dnsPropagated", tone: "info" },
  verified: { label: "dnsVerified", tone: "success" },
  failed: { label: "statusFailed", tone: "danger" },
};

export const ORDER_STEP: Readonly<Record<OrderStep, { title: Label; hint: Label }>> = {
  paid: { title: "stepPaidTitle", hint: "stepPaidHint" },
  domain: { title: "stepDomainTitle", hint: "stepDomainHint" },
  dns: { title: "stepDnsTitle", hint: "stepDnsHint" },
  mailboxes: { title: "stepMailboxesTitle", hint: "stepMailboxesHint" },
  warmup: { title: "stepWarmupTitle", hint: "stepWarmupHint" },
};

/** A price in euros, written the way the active language writes money. */
export function euros(cents: number, format: Formats): string {
  return format.number(cents / 100, { style: "currency", currency: "EUR" });
}

/** A day with its year, or `-` when there is none. */
export function formatDate(iso: string | null, format: Formats): string {
  return iso ? format.date(iso, { day: "numeric", month: "short", year: "numeric" }) : "-";
}

function clean(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

/** The address part before the @ suggested from a sender's name. */
export function localPartsFor(firstName: string, lastName: string): string[] {
  const first = clean(firstName);
  const last = clean(lastName);
  if (!first) return [];
  return [first, last ? `${first}.${last}` : `hello`, last ? `${first.charAt(0)}${last}` : `${first}1`];
}
